import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Table,
  Row,
  Col,
  Input,
  Nav,
  NavItem,
  NavLink,
  Badge,
  FormGroup,
  Label,
} from "reactstrap";
import {
  formatDateForDisplay,
  formatDateForAPI,
  formatDateTimeForDisplay,
} from "utils/dateUtils";
import { formatPriceWithDollar, toNumber } from "utils/currency";
import {
  MdCheckCircle,
  MdChevronRight,
  MdAdd,
  MdRemove,
  MdEmail,
  MdMessage,
  MdInfo,
} from "react-icons/md";
import { IoMdCloseCircle } from "react-icons/io";
import { RiErrorWarningFill } from "react-icons/ri";
import { useGetWorksheetDetail } from "api/worksheet/service";
import { useUpdateWorksheet, usePrintWorksheet } from "api/worksheet/service";
import { useSendDealSMS, useUpdateDeal } from "api/deals/service";
import Select from "components/Select";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterBorderColor,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import AddVehicle from "views/components/Deals/AddVehicle";
import StatesInput from "components/StatesInput";
import { useGetSalesLocations } from "api/sales-locations/service";
import { getSelectStyles } from "utils/selectStyles";
import { useGetDocuments } from "api/documents/service";
import { useSendMail } from "api/mails/service";
import SendEmail from "components/SendEmail";
import { useNotification } from "contexts/NotificationContext";
import { filterDocumentsForWorksheetType } from "utils/worksheetPrintDocuments";

const DealsRightDrawer = forwardRef(function DealsRightDrawer(
  {
    activeColorStyle,
    activeColor = "blue",
    worksheetId,
    dealId,
    onNavigateToWorksheets,
    onAddTradeIn,
    decisionHistory,
    contractSummary,
    applicantData,
    hasTradeIn1,
    hasTradeIn2,
    onRemoveTradeIn1,
    onAddTradeIn2,
    onRemoveTradeIn2,
    onAddDesiredVehicle,
    onRemoveDesiredVehicle,
    hasDesiredVehicle,
    isWorksheet,
    shellThemeVariant = "1",
  },
  ref
) {

  const { t } = useTranslation();
  const isTheme2 = shellThemeVariant === "2";
  const theme2SelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const drawerFormFieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".deals-right-drawer.deals-right-drawer--theme2",
            fieldsSelector: ".deals-right-drawer-form-fields",
          })
        : "",
    [isTheme2]
  );
  const DrawerSelect = useCallback(
    ({ styles, className = "react-select", ...selectProps }) => (
      <Select
        {...selectProps}
        className={className}
        styles={isTheme2 ? (styles ?? theme2SelectStyles) : styles}
      />
    ),
    [isTheme2, theme2SelectStyles]
  );
  const { showNotification } = useNotification();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState(null);
  const [sendView, setSendView] = useState("options"); // 'options', 'text_auth'
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [isLightTheme, setIsLightTheme] = useState(false);
  const [drawerActiveTab, setDrawerActiveTab] = useState("action");
  const [drawerFullSize, setDrawerFullSize] = useState(false);
  const [drawerSubView, setDrawerSubView] = useState(null); // null, 'add_desired_vehicle'
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const drawerWidthDragRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const activeWorksheetId = isWorksheet && worksheetId ? worksheetId : undefined;
  const { data: worksheetData } = useGetWorksheetDetail(activeWorksheetId);
  const updateWorksheetMutation = useUpdateWorksheet();
  const printWorksheetMutation = usePrintWorksheet();
  const sendDealSMSMutation = useSendDealSMS();
  const updateDealMutation = useUpdateDeal();
  const [decisionStatus, setDecisionStatus] = useState("");
  const [decisionNotes, setDecisionNotes] = useState("");
  const [approvalCode, setApprovalCode] = useState("");
  const [printSubView, setPrintSubView] = useState("menu");
  const [sourceView, setSourceView] = useState("menu");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [pdfUrl, setPdfUrl] = useState(null);

  const resetPrintDrawerState = useCallback(() => {
    setPrintSubView("menu");
    setSourceView("menu");
    setSelectedDocuments([]);
    setDrawerFullSize(false);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);
  const { data: documentsData, isLoading: isDocumentsLoading } = useGetDocuments("worksheet");
  const sendMailMutation = useSendMail();
  const [sendEmailData, setSendEmailData] = useState(null);
  const [salesTaxZeroAcknowledged, setSalesTaxZeroAcknowledged] = useState(false);

  /* useEffect(() => {
    if (documentsData?.data) {
      const allIds = documentsData.data.map((d) => d.id);
      setSelectedDocuments(allIds);
    }
  }, [documentsData]); */

  // Cleanup PDF URL when drawer closes or component unmounts
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Settings form state
  const [settingsData, setSettingsData] = useState({
    salesLocation: "",
    allowDealerRebates: false,
    allowMfrRebates: false,
    contractState: "",
    registrationState: null,
    documentPackage: "",
    retailPurchaseAgreement: "",
    contract: "",
    compoundingMethod: "",
    daysInYear: "",
    complianceLevel: "",
    disclosureMethod: "",
    amortizationMethod: "US Rule",
    salesTaxDeferred: "Yes",
    allowLenderVSI: "No",
    maxNumOfPaymentSchedules: "2",
    allowPrepaidFinanceCharge: "No",
    numOfDefDownsAllowed: 6,
    useMaxRate: false,
  });

  const { data: salesLocationsData, isLoading: isLoadingSalesLocations } =
    useGetSalesLocations(1, 1000, "");
  const customSelectStyles = useMemo(() => {
    return getSelectStyles(activeColorStyle, isLightTheme);
  }, [activeColorStyle, isLightTheme]);
  const getDrawerFieldBorderColor = useCallback(
    () =>
      isTheme2
        ? getTheme2SearchFilterBorderColor()
        : isLightTheme
          ? "rgba(0,0,0,0.1)"
          : "rgba(255,255,255,0.1)",
    [isTheme2, isLightTheme]
  );

  const salesLocationOptions = useMemo(() => {
    const results =
      salesLocationsData?.data || salesLocationsData?.results || [];
    return results.map((location) => ({
      value: location.id || location.documentId,
      label: location.name || location.channel?.name || "",
    }));
  }, [salesLocationsData]);
  const worksheetDecisionLabel = useMemo(() => {
    return worksheetData?.worksheet_status;
  }, [worksheetData?.worksheet_status]);

  const shouldShowDecisionFailure = useMemo(() => {
    if (!worksheetDecisionLabel) return true;
    const normalized = worksheetDecisionLabel.toLowerCase();
    return ![
      "approved",
      "contracted",
      "contracted_booked",
      "loan_booked",
    ].includes(normalized);
  }, [worksheetDecisionLabel]);

  const contractDateMismatch = useMemo(() => {
    if (!worksheetData?.contract_date) {
      return false;
    }
    const normalizedContractDate = formatDateForAPI(
      worksheetData.contract_date
    );
    // Get today's date in local timezone (year, month, day only, no time)
    const today = new Date();
    const todayLocal = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const normalizedToday = formatDateForAPI(todayLocal);
    // Show warning only if contract_date is in the past (before today)
    return normalizedContractDate < normalizedToday;
  }, [worksheetData?.contract_date]);

  const salesTaxAmount = useMemo(() => {
    const washoutTaxes = worksheetData?.washout?.taxes;
    if (washoutTaxes != null && washoutTaxes !== "") {
      return toNumber(washoutTaxes) || 0;
    }
    if (worksheetData?.taxes != null && worksheetData?.taxes !== "") {
      return toNumber(worksheetData.taxes) || 0;
    }
    const rows = Array.isArray(worksheetData?.tax_and_fees)
      ? worksheetData.tax_and_fees
      : [];
    return rows
      .filter((row) => String(row?.type || "").toLowerCase() === "tax")
      .reduce((sum, row) => sum + (toNumber(row?.amount) || 0), 0);
  }, [worksheetData]);

  const isSalesTaxZero = Math.abs(salesTaxAmount) < 0.009;

  const isPendingContractAction = useMemo(() => {
    const status = String(worksheetData?.worksheet_status || "").toLowerCase();
    return status !== "contracted" && status !== "loan_booked" && status !== "contracted_booked";
  }, [worksheetData?.worksheet_status]);

  const requireSalesTaxConfirm =
    isSalesTaxZero && isPendingContractAction && !salesTaxZeroAcknowledged;

  const isWholesale = useMemo(() => {
    return applicantData?.type === "dealer_wholesaler_auction";
  }, [applicantData]);

  // Check if there are any red (fail) errors
  const hasNoRedErrors = useMemo(() => {
    const hasInventory = worksheetData?.inventory != null;
    const isApproved = !shouldShowDecisionFailure;
    const hasAddress = isWholesale
      ? !!applicantData?.address_1
      : !!applicantData?.current_address?.address_1 &&
      !!applicantData?.current_address?.city &&
      !!applicantData?.current_address?.state;
    return hasInventory && isApproved && hasAddress;
  }, [
    worksheetData?.inventory,
    shouldShowDecisionFailure,
    isWholesale,
    applicantData,
  ]);

  // Check if there are any errors or warnings at all
  const hasAnyErrors = useMemo(() => {
    const hasInventoryError = worksheetData?.inventory == null;
    const hasDecisionError = shouldShowDecisionFailure;
    const hasContractDateWarning = contractDateMismatch;
    const hasSsnWarning = !isWholesale && applicantData?.ssn === "";
    const hasDobWarning = !isWholesale && !applicantData?.date_of_birth;
    const hasAddressError = isWholesale
      ? !applicantData?.address_1
      : !applicantData?.current_address?.address_1 ||
      !applicantData?.current_address?.city ||
      !applicantData?.current_address?.state;
    return (
      hasInventoryError ||
      hasDecisionError ||
      hasContractDateWarning ||
      hasSsnWarning ||
      hasDobWarning ||
      hasAddressError
    );
  }, [
    worksheetData?.inventory,
    shouldShowDecisionFailure,
    contractDateMismatch,
    isWholesale,
    applicantData,
  ]);

  const formattedContractDate = useMemo(() => {
    return formatDateForDisplay(worksheetData?.contract_date);
  }, [worksheetData?.contract_date]);

  const formattedStatusChangeDate = useMemo(() => {
    return worksheetData?.status_change_date
      ? formatDateTimeForDisplay(worksheetData.status_change_date)
      : "-";
  }, [worksheetData?.status_change_date]);

  useEffect(() => {
    if (worksheetData) {
      setDecisionStatus(worksheetData?.worksheet_status);
      setDecisionNotes(worksheetData?.notes);
    }
  }, [worksheetData]);

  // If worksheet is contracted or contracted_booked, switch to contract tab if decision tab is active
  useEffect(() => {
    if (drawerActiveTab === "contract") {
      setSalesTaxZeroAcknowledged(false);
    }
  }, [drawerActiveTab, worksheetId]);

  useEffect(() => {
    if (
      ["contracted", "contracted_booked", "loan_booked"].includes(
        worksheetData?.worksheet_status
      ) &&
      drawerActiveTab === "decision"
    ) {
      setDrawerActiveTab("contract");
    }
  }, [worksheetData?.worksheet_status, drawerActiveTab]);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains("white-content"));
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    resetPrintDrawerState();
  }, [resetPrintDrawerState]);

  const endDrawerWidthResizeInteraction = useCallback((clientX) => {
    const drag = drawerWidthDragRef.current;
    if (!drag) return;
    drawerWidthDragRef.current = null;
    const dx = clientX - drag.startX;
    if (Math.abs(dx) < 14) {
      setDrawerFullSize(!drag.startFull);
    } else if (dx < -42) {
      setDrawerFullSize(true);
    } else if (dx > 42) {
      setDrawerFullSize(false);
    }
  }, []);

  const onDrawerWidthHandlePointerDown = useCallback(
    (e) => {
      if (isMobile) return;
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget;
      if (typeof el.setPointerCapture === "function") {
        el.setPointerCapture(e.pointerId);
      }
      drawerWidthDragRef.current = {
        startX: e.clientX,
        startFull: drawerFullSize,
      };
      const onMove = () => {};
      const onUp = (ev) => {
        endDrawerWidthResizeInteraction(ev.clientX);
        try {
          if (typeof el.releasePointerCapture === "function") {
            el.releasePointerCapture(ev.pointerId);
          }
        } catch (_) {
          /* ignore */
        }
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
      };
      el.addEventListener("pointermove", onMove, { passive: true });
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
    },
    [isMobile, drawerFullSize, endDrawerWidthResizeInteraction]
  );

  useImperativeHandle(
    ref,
    () => ({
      open: (type, initialData = null) => {
        setDrawerType(type);
        setSendEmailData(initialData);
        setSalesTaxZeroAcknowledged(false);
        resetPrintDrawerState();
        if (initialData) {
          setSendView("email");
        } else {
          setSendView("options");
        }
        if (type === "deals") {
          setDrawerActiveTab("contract");
        }
        setIsDrawerVisible(true);
        const start = () => setDrawerOpen(true);
        if (
          typeof window !== "undefined" &&
          typeof window.requestAnimationFrame === "function"
        ) {
          window.requestAnimationFrame(start);
        } else {
          setTimeout(start, 0);
        }
      },
      close: () => closeDrawer(),
    }),
    [closeDrawer, resetPrintDrawerState]
  );

  useEffect(() => {
    if (!drawerOpen && isDrawerVisible) {
      const timer = setTimeout(() => {
        setIsDrawerVisible(false);
        setDrawerType(null);
        resetPrintDrawerState();
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [drawerOpen, isDrawerVisible, resetPrintDrawerState]);

  const drawerStyles = useMemo(
    () => ({
      overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.35)",
        zIndex: 2000,
        opacity: 0,
        transition: "opacity 300ms ease",
      },
      panel: {
        position: "fixed",
        top: 0,
        right: 0,
        width: isMobile ? "100%" : (drawerFullSize ? "85vw" : "45vw"),
        maxWidth: isMobile ? "100%" : "90vw",
        height: "100vh",
        backgroundColor: isLightTheme ? "#fff" : "#1e1e2f",
        color: isLightTheme ? "#1e1e2f" : "#fff",
        boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
        zIndex: 2001,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        transform: "translateX(100%)",
        transition: "transform 300ms ease, width 220ms ease, max-width 220ms ease",
      },
      drawerResizeHandle: {
        width: 18,
        flexShrink: 0,
        cursor: "ew-resize",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        borderRight: isLightTheme
          ? "1px solid rgba(0,0,0,0.08)"
          : "1px solid rgba(255,255,255,0.12)",
        backgroundColor: isLightTheme
          ? "rgba(0,0,0,0.03)"
          : "rgba(255,255,255,0.04)",
      },
      mainColumn: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      },
      header: {
        padding: "0px 16px",
        flexShrink: 0,
        borderBottom: isLightTheme
          ? "1px solid rgba(0,0,0,0.1)"
          : "1px solid  rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      },
      body: {
        padding: "8px 16px 16px 16px",
        overflow: "auto",
        flex: 1,
        minHeight: 0,
      },
      closeBtn: {
        ...activeColorStyle,
        color: "white",
        border: 0,
        padding: "6px 12px",
        fontWeight: 600,
        cursor: "pointer",
      },
    }),
    [activeColorStyle, isLightTheme, drawerFullSize, isMobile]
  );

  const normalizedContractSummary = useMemo(() => {
    const summary = contractSummary || {};
    let salesPriceDisplay = "-";
    if (
      summary.salesPrice !== undefined &&
      summary.salesPrice !== null &&
      summary.salesPrice !== ""
    ) {
      if (typeof summary.salesPrice === "number") {
        salesPriceDisplay = formatPriceWithDollar(summary.salesPrice);
      } else {
        const numeric = Number(
          String(summary.salesPrice).replace(/[^\d.-]/g, "")
        );
        if (!Number.isNaN(numeric)) {
          salesPriceDisplay = formatPriceWithDollar(numeric);
        } else {
          salesPriceDisplay = String(summary.salesPrice);
        }
      }
    }

    return {
      contractDate: summary.contractDate || "-",
      lender: summary.lender || "-",
      salesPrice: salesPriceDisplay,
      vehicle: summary.vehicle || "-",
    };
  }, [contractSummary]);

  const dealsDrawerStaticHeaderTitle = useMemo(() => {
    switch (drawerType) {
      case "settings":
        return t("dealsDrawer.headers.settings");
      case "print":
        if (printSubView === "viewer") {
          return t("dealsDrawer.headers.documentViewer");
        }
        if (printSubView === "documents") {
          return t("dealsDrawer.headers.printDocuments");
        }
        return t("dealsDrawer.headers.printOptions");
      case "send":
        if (sendView === "text_auth") {
          return t("dealsDrawer.headers.textAuthorization");
        }
        if (sendView === "email") {
          return t("dealsDrawer.headers.sendEmail");
        }
        return t("dealsDrawer.headers.sendOptions");
      case "more":
        return t("dealsDrawer.headers.moreOptions");
      default:
        return "";
    }
  }, [drawerType, printSubView, sendView, t]);

  const renderContractSummary = () => (
    <div>
      <Row>
        <Col md="6" className="mb-3">
          <label className="text-uppercase small font-weight-bold mb-1">
            {t("dealsDrawer.contractDeal.fields.contractDate")}
          </label>
          <Input
            value={formattedContractDate}
            bsSize="sm"
            readOnly
            disabled
            style={{
              height: 35,
              backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
              color: isLightTheme ? "#1e1e2f" : "#fff",
              borderColor: isLightTheme
                ? "rgba(0,0,0,0.1)"
                : "rgba(255,255,255,0.1)",
            }}
          />
        </Col>
        <Col md="6" className="mb-3">
          <label className="text-uppercase small font-weight-bold mb-1">
            {t("dealsDrawer.contractDeal.fields.lender")}
          </label>
          <Input
            value={normalizedContractSummary.lender}
            bsSize="sm"
            readOnly
            disabled
            style={{
              height: 35,
              backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
              color: isLightTheme ? "#1e1e2f" : "#fff",
              borderColor: isLightTheme
                ? "rgba(0,0,0,0.1)"
                : "rgba(255,255,255,0.1)",
            }}
          />
        </Col>
        <Col md="6" className="mb-3">
          <label className="text-uppercase small font-weight-bold mb-1">
            {t("dealsDrawer.contractDeal.fields.salesPrice")}
          </label>
          <Input
            value={normalizedContractSummary.salesPrice}
            bsSize="sm"
            readOnly
            disabled
            style={{
              height: 35,
              backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
              color: isLightTheme ? "#1e1e2f" : "#fff",
              borderColor: isLightTheme
                ? "rgba(0,0,0,0.1)"
                : "rgba(255,255,255,0.1)",
            }}
          />
        </Col>
        <Col md="6" className="mb-3">
          <label className="text-uppercase small font-weight-bold mb-1">
            {t("dealsDrawer.contractDeal.fields.vehicle")}
          </label>
          <Input
            value={normalizedContractSummary.vehicle}
            bsSize="sm"
            readOnly
            disabled
            style={{
              height: 35,
              backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
              color: isLightTheme ? "#1e1e2f" : "#fff",
              borderColor: isLightTheme
                ? "rgba(0,0,0,0.1)"
                : "rgba(255,255,255,0.1)",
            }}
          />
        </Col>
      </Row>

      {hasAnyErrors && (
        <>
          <h5 className="mt-4 mb-2 text-uppercase font-weight-bold">
            {t("dealsDrawer.contractDeal.auditResults")}
          </h5>

          <div className="table-responsive">
            <Table bordered size="sm" className="mb-0">
              <thead
                style={{
                  backgroundColor: isLightTheme ? "#f0f3ff" : "#2d3a5b",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                }}
              >
                <tr>
                  <th>{t("dealsDrawer.contractDeal.table.result")}</th>
                  <th>{t("dealsDrawer.contractDeal.table.description")}</th>
                  <th>{t("dealsDrawer.contractDeal.table.message")}</th>
                </tr>
              </thead>
              <tbody>
                {worksheetData?.inventory == null && (
                  <tr>
                    <td className="text-center">
                      <IoMdCloseCircle size={20} color="#ff5c5c" />
                    </td>
                    <td>Check Selected Inventory</td>
                    <td>No inventory has been selected for this worksheet</td>
                  </tr>
                )}
                {shouldShowDecisionFailure && (
                  <tr>
                    <td className="text-center">
                      <IoMdCloseCircle size={20} color="#ff5c5c" />
                    </td>
                    <td>Check worksheet decision</td>
                    <td>
                      Current worksheet has not been approved. Current decision:{" "}
                      {worksheetDecisionLabel
                        ? worksheetDecisionLabel
                          .replace(/_/g, " ")
                          .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
                        : "-"}
                    </td>
                  </tr>
                )}
                {contractDateMismatch && (
                  <tr>
                    <td className="text-center">
                      <RiErrorWarningFill size={20} color="#ffb020" />
                    </td>
                    <td>Check contract date</td>
                    <td>
                      Worksheet Contract Date ({formattedContractDate}) is a
                      date other than today
                    </td>
                  </tr>
                )}
                {!isWholesale && applicantData?.ssn === "" && (
                  <tr>
                    <td className="text-center">
                      <RiErrorWarningFill size={20} color="#ffb020" />
                    </td>
                    <td>Check Primary Buyer SSN</td>
                    <td>Primary Buyer does NOT have a valid SSN</td>
                  </tr>
                )}
                {!isWholesale && !applicantData?.date_of_birth && (
                  <tr>
                    <td className="text-center">
                      <RiErrorWarningFill size={20} color="#ffb020" />
                    </td>
                    <td>Check Primary Buyer DOB</td>
                    <td>Primary Buyer DOB is missing</td>
                  </tr>
                )}
                {isWholesale ? (
                  !applicantData?.address_1 && (
                    <tr>
                      <td className="text-center">
                        <IoMdCloseCircle size={20} color="#ff5c5c" />
                      </td>
                      <td>Check Wholesaler Address</td>
                      <td>Wholesaler does NOT have a valid Address</td>
                    </tr>
                  )
                ) : (
                  (!applicantData?.current_address?.address_1 ||
                    !applicantData?.current_address?.city ||
                    !applicantData?.current_address?.state) && (
                    <tr>
                      <td className="text-center">
                        <IoMdCloseCircle size={20} color="#ff5c5c" />
                      </td>
                      <td>Check Primary Buyer Address</td>
                      <td>
                        Primary Buyer Home Address is incomplete (Address, City
                        and State are required)
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      {hasNoRedErrors && (
        <>
          {isSalesTaxZero && isPendingContractAction && (
            <div
              className="mt-3 mb-2 d-flex align-items-center justify-content-between"
              style={{
                gap: 12,
                padding: "10px 12px",
                backgroundColor: isLightTheme
                  ? "rgba(255, 176, 32, 0.12)"
                  : "rgba(255, 176, 32, 0.15)",
                border: "1px solid rgba(255, 176, 32, 0.55)",
              }}
            >
              <div
                className="d-flex align-items-center flex-grow-1"
                style={{ gap: 8, minWidth: 0 }}
              >
                <RiErrorWarningFill
                  size={22}
                  color="#ffb020"
                  style={{ flexShrink: 0 }}
                />
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    color: isLightTheme ? "#1e1e2f" : "#fff",
                    lineHeight: 1.35,
                    textAlign: "center",
                  }}
                >
                  {t("dealsDrawer.contractDeal.salesTaxZeroWarning")}
                </span>
              </div>
              {!salesTaxZeroAcknowledged && (
                <Button
                  color="warning"
                  size="sm"
                  className="mb-0 flex-shrink-0"
                  onClick={() => setSalesTaxZeroAcknowledged(true)}
                  style={{ fontWeight: 700, minWidth: 56 }}
                >
                  {t("dealsDrawer.contractDeal.salesTaxZeroYes")}
                </Button>
              )}
            </div>
          )}
          <div className="mt-2 justify-content-end w-100 d-flex gap-2">
            <Button
              color={
                worksheetData?.worksheet_status === "contracted"
                  ? "primary"
                  : "secondary"
              }
              disabled={requireSalesTaxConfirm}
              onClick={() => {
                updateWorksheetMutation.mutate({
                  id: worksheetId,
                  dealId,
                  data: {
                    contracted:
                      worksheetData?.worksheet_status === "loan_booked"
                        ? false
                        : true,
                  },
                });
              }}
            >
              {worksheetData?.worksheet_status === "contracted"
                ? "Contract & Book the Deal"
                : worksheetData?.worksheet_status === "loan_booked"
                  ? "Un-Book the Deal"
                  : "Contract & Book the Deal"}
            </Button>
            {worksheetData?.worksheet_status !== "loan_booked" && (
              <Button
                color={
                  worksheetData?.worksheet_status === "contracted"
                    ? "red"
                    : "secondary"
                }
                disabled={
                  requireSalesTaxConfirm &&
                  worksheetData?.worksheet_status !== "contracted"
                }
                onClick={() => {
                  updateWorksheetMutation.mutate({
                    id: worksheetId,
                    dealId,
                    data: {
                      only_contracted:
                        worksheetData?.worksheet_status === "contracted"
                          ? false
                          : true,
                    },
                  });
                }}
              >
                {worksheetData?.worksheet_status === "contracted"
                  ? "Un-Contract Deal"
                  : "Only Contract the Deal"}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderDecisionContent = () => (
    <div>
      <div className="mt-0 ">
        <Row>
          <Col md="6" className="mb-3">
            <label className="text-uppercase small font-weight-bold mb-1">
              Status
            </label>
            <DrawerSelect
              activeColor={activeColor}
              className="react-select"
              name="decisionStatus"
              placeholder="Select status"
              isSearchable={false}
              closeMenuOnSelect
              value={
                decisionStatus
                  ? {
                    value: decisionStatus,
                    label: decisionStatus
                      .replace(/_/g, " ")
                      .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase()),
                  }
                  : null
              }
              onChange={(option) => setDecisionStatus(option?.value || "")}
              options={[
                "working",
                "pending_decision",
                "acknowledged",
                "approved",
                "declined",
                "conditional_approval",
                "cancelled",
                "withdrawn",
                "pending",
                "error",
                "more_info_needed",
              ].map((value) => ({
                value,
                label: value
                  .replace(/_/g, " ")
                  .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase()),
              }))}
            />
            <div className="text-muted small mt-2">
              Last updated: {formattedStatusChangeDate}
            </div>
          </Col>
          <Col md="12" className="mb-3">
            <label className="text-uppercase small font-weight-bold mb-1">
              Notes
            </label>
            <Input
              type="textarea"
              rows={4}
              className={isTheme2 ? "form-control" : undefined}
              value={decisionNotes}
              onChange={(event) => setDecisionNotes(event.target.value)}
              style={{
                backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                color: isLightTheme ? "#1e1e2f" : "#fff",
                padding: "8px 11px",
                borderColor: isTheme2
                  ? getTheme2SearchFilterBorderColor()
                  : isLightTheme
                    ? "rgba(0,0,0,0.1)"
                    : "rgba(255,255,255,0.1)",
              }}
            />
          </Col>
        </Row>
        <div className="d-flex justify-content-end">
          <Button
            size="sm"
            style={{
              ...activeColorStyle,
              color: "#fff",
              border: 0,
              fontWeight: 600,
              minWidth: 100,
            }}
            onClick={() => {
              if (!worksheetId) return;
              updateWorksheetMutation.mutate({
                id: worksheetId,
                dealId,
                data: {
                  worksheet_status: decisionStatus,
                  notes: decisionNotes,
                  status_change_date: new Date().toISOString(),
                },
              });
            }}
            disabled={updateWorksheetMutation.isPending || !decisionStatus}
          >
            {updateWorksheetMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("common.saving")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderDealsContent = () => (
    <div>
      <div className="mt-0">
        {drawerActiveTab === "contract"
          ? renderContractSummary()
          : renderDecisionContent()}
      </div>
    </div>
  );

  const handleSettingsChange = (field, value) => {
    setSettingsData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNumberChange = (field, delta) => {
    setSettingsData((prev) => ({
      ...prev,
      [field]: Math.max(0, (prev[field] || 0) + delta),
    }));
  };

  const handleSendAuthorization = () => {
    if (!dealId) {
      console.error("Deal ID is missing");
      return;
    }
    sendDealSMSMutation.mutate(dealId);
  };

  const handleVerifyApprovalCode = () => {
    if (!dealId) {
      console.error("Deal ID is missing");
      return;
    }
    if (!approvalCode || approvalCode.trim() === "") {
      console.error("Approval code is required");
      return;
    }

    // Update deal with approval code
    updateDealMutation.mutate({
      id: dealId,
      data: {
        allow_code: approvalCode.trim()
      }
    });
  };

  const renderSettingsContent = () => (
    <div>
      {/* Location Settings */}
      <div className="mb-0">
        <h6
          className="mb-0"
          style={{
            fontWeight: 700,
            textTransform: "uppercase",
            fontSize: 12,
            letterSpacing: "0.05em",
            color: isLightTheme ? "#1e1e2f" : "#fff",
          }}
        >
          Location Settings
        </h6>
        <Row>
          <Col md="12" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Sales Location
              </Label>
              <DrawerSelect
                activeColor={activeColor}
                className="react-select"
                placeholder="Select Sales Location"
                isSearchable
                closeMenuOnSelect={true}
                value={
                  settingsData.salesLocation
                    ? {
                      value: settingsData.salesLocation,
                      label:
                        salesLocationOptions.find(
                          (opt) => opt.value === settingsData.salesLocation
                        )?.label || "",
                    }
                    : null
                }
                onChange={(option) =>
                  handleSettingsChange("salesLocation", option?.value || "")
                }
                options={salesLocationOptions}
                isLoading={isLoadingSalesLocations}
                isClearable
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Allow Dealer Rebates
              </Label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "0px",
                }}
              >
                <div className="custom-toggle">
                  <Input
                    id="allowDealerRebates"
                    name="allowDealerRebates"
                    type="checkbox"
                    checked={!!settingsData.allowDealerRebates}
                    onChange={(e) =>
                      handleSettingsChange(
                        "allowDealerRebates",
                        e.target.checked
                      )
                    }
                  />
                  <span className="custom-toggle-slider" />
                </div>
              </div>
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                }}
              >
                Allow Mfr Rebates
              </Label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "0px",
                }}
              >
                <div className="custom-toggle">
                  <Input
                    id="allowMfrRebates"
                    name="allowMfrRebates"
                    type="checkbox"
                    checked={!!settingsData.allowMfrRebates}
                    onChange={(e) =>
                      handleSettingsChange("allowMfrRebates", e.target.checked)
                    }
                  />
                  <span className="custom-toggle-slider" />
                </div>
              </div>
            </FormGroup>
          </Col>
          <Col md="6" className="mb-3">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 0,
                }}
              >
                Contract State
              </Label>
              <Input
                type="text"
                className={isTheme2 ? "form-control" : undefined}
                value={settingsData.contractState}
                onChange={(e) =>
                  handleSettingsChange("contractState", e.target.value)
                }
                placeholder="TX"
                style={{
                  backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                  borderColor: getDrawerFieldBorderColor(),
                }}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-2">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 0,
                }}
              >
                Registration State
              </Label>
              <StatesInput
                name="registrationState"
                activeColor={activeColor}
                className="react-select"
                value={settingsData.registrationState}
                onChange={(stateObject) => {
                  handleSettingsChange(
                    "registrationState",
                    stateObject
                      ? {
                        id: stateObject.id,
                        abbreviation: stateObject.abbreviation,
                      }
                      : null
                  );
                }}
                placeholder="Select State"
                customSelectStyles={
                  isTheme2 ? theme2SelectStyles : customSelectStyles
                }
              />
            </FormGroup>
          </Col>
        </Row>
      </div>

      {/* Contract Settings */}
      <div className="mb-0">
        <h6
          className="mb-0"
          style={{
            fontWeight: 700,
            textTransform: "uppercase",
            fontSize: 12,
            letterSpacing: "0.05em",
            color: isLightTheme ? "#1e1e2f" : "#fff",
          }}
        >
          Contract Settings
        </h6>
        <Row>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Document Package
              </Label>
              <DrawerSelect
                activeColor={activeColor}
                className="react-select"
                placeholder="Select Document Package"
                isSearchable
                closeMenuOnSelect={true}
                value={
                  settingsData.documentPackage
                    ? {
                      value: settingsData.documentPackage,
                      label: settingsData.documentPackage,
                    }
                    : null
                }
                onChange={(option) =>
                  handleSettingsChange("documentPackage", option?.value || "")
                }
                options={[]}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Retail Purchase Agreement
              </Label>
              <DrawerSelect
                activeColor={activeColor}
                className="react-select"
                placeholder="Select Retail Purchase Agreement"
                isSearchable
                closeMenuOnSelect={true}
                value={
                  settingsData.retailPurchaseAgreement
                    ? {
                      value: settingsData.retailPurchaseAgreement,
                      label: settingsData.retailPurchaseAgreement,
                    }
                    : null
                }
                onChange={(option) =>
                  handleSettingsChange(
                    "retailPurchaseAgreement",
                    option?.value || ""
                  )
                }
                options={[]}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Contract
              </Label>
              <DrawerSelect
                activeColor={activeColor}
                className="react-select"
                placeholder="Select Contract"
                isSearchable
                closeMenuOnSelect={true}
                value={
                  settingsData.contract
                    ? {
                      value: settingsData.contract,
                      label: settingsData.contract,
                    }
                    : null
                }
                onChange={(option) =>
                  handleSettingsChange("contract", option?.value || "")
                }
                options={[]}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Compounding Method
              </Label>
              <DrawerSelect
                activeColor={activeColor}
                className="react-select"
                placeholder="Select Compounding Method"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={
                  settingsData.compoundingMethod
                    ? {
                      value: settingsData.compoundingMethod,
                      label: settingsData.compoundingMethod,
                    }
                    : null
                }
                onChange={(option) =>
                  handleSettingsChange("compoundingMethod", option?.value || "")
                }
                options={[{ value: "Exact Days", label: "Exact Days" }]}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Days In Year
              </Label>
              <DrawerSelect
                activeColor={activeColor}
                className="react-select"
                placeholder="Select Days In Year"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={
                  settingsData.daysInYear
                    ? {
                      value: settingsData.daysInYear,
                      label: settingsData.daysInYear,
                    }
                    : null
                }
                onChange={(option) =>
                  handleSettingsChange("daysInYear", option?.value || "")
                }
                options={[{ value: "365 Days", label: "365 Days" }]}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Compliance Level
              </Label>
              <DrawerSelect
                activeColor={activeColor}
                className="react-select"
                placeholder="Select Compliance Level"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={
                  settingsData.complianceLevel
                    ? {
                      value: settingsData.complianceLevel,
                      label: settingsData.complianceLevel,
                    }
                    : null
                }
                onChange={(option) =>
                  handleSettingsChange("complianceLevel", option?.value || "")
                }
                options={[{ value: "Level 2", label: "Level 2" }]}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Disclosure Method
              </Label>
              <DrawerSelect
                activeColor={activeColor}
                className="react-select"
                placeholder="Select Disclosure Method"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={
                  settingsData.disclosureMethod
                    ? {
                      value: settingsData.disclosureMethod,
                      label: settingsData.disclosureMethod,
                    }
                    : null
                }
                onChange={(option) =>
                  handleSettingsChange("disclosureMethod", option?.value || "")
                }
                options={[{ value: "Use REGZ APR", label: "Use REGZ APR" }]}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Amortization Method
              </Label>
              <Input
                type="text"
                className={isTheme2 ? "form-control" : undefined}
                value={settingsData.amortizationMethod}
                readOnly
                disabled
                style={{
                  backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                  borderColor: getDrawerFieldBorderColor(),
                }}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Sales Tax Deferred
              </Label>
              <Input
                type="text"
                className={isTheme2 ? "form-control" : undefined}
                value={settingsData.salesTaxDeferred}
                readOnly
                disabled
                style={{
                  backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                  borderColor: getDrawerFieldBorderColor(),
                }}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Allow Lender VSI
              </Label>
              <Input
                type="text"
                className={isTheme2 ? "form-control" : undefined}
                value={settingsData.allowLenderVSI}
                readOnly
                disabled
                style={{
                  backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                  borderColor: getDrawerFieldBorderColor(),
                }}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Max Num Of Payment Schedules
              </Label>
              <Input
                type="text"
                className={isTheme2 ? "form-control" : undefined}
                value={settingsData.maxNumOfPaymentSchedules}
                readOnly
                disabled
                style={{
                  backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                  borderColor: getDrawerFieldBorderColor(),
                }}
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Allow Prepaid Finance Charge
              </Label>
              <Input
                type="text"
                className={isTheme2 ? "form-control" : undefined}
                value={settingsData.allowPrepaidFinanceCharge}
                readOnly
                disabled
                style={{
                  backgroundColor: isLightTheme ? "#f5f6fa" : "#2b2b3d",
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                  borderColor: getDrawerFieldBorderColor(),
                }}
              />
            </FormGroup>
          </Col>
        </Row>
      </div>

      {/* Other Settings */}
      <div className="mb-4">
        <h6
          className="mb-3"
          style={{
            fontWeight: 700,
            textTransform: "uppercase",
            fontSize: 12,
            letterSpacing: "0.05em",
            color: isLightTheme ? "#1e1e2f" : "#fff",
          }}
        >
          Other Settings
        </h6>
        <Row className="d-flex align-items-center">
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Num Of Def Downs Allowed
              </Label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Button
                  size="sm"
                  onClick={() => handleNumberChange("numOfDefDownsAllowed", -1)}
                  style={{
                    ...activeColorStyle,
                    color: "#fff",
                    minWidth: "36px",
                    height: "36px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                  }}
                >
                  <MdRemove size={20} />
                </Button>
                <Input
                  type="number"
                  className={isTheme2 ? "form-control" : undefined}
                  value={settingsData.numOfDefDownsAllowed}
                  onChange={(e) =>
                    handleSettingsChange(
                      "numOfDefDownsAllowed",
                      parseInt(e.target.value) || 0
                    )
                  }
                  style={{
                    flex: 1,
                    textAlign: "center",
                    backgroundColor: isLightTheme ? "#fff" : "#2b2b3d",
                    color: isLightTheme ? "#1e1e2f" : "#fff",
                    borderColor: getDrawerFieldBorderColor(),
                  }}
                />
                <Button
                  size="sm"
                  onClick={() => handleNumberChange("numOfDefDownsAllowed", 1)}
                  style={{
                    ...activeColorStyle,
                    color: "#fff",
                    minWidth: "36px",
                    height: "36px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                  }}
                >
                  <MdAdd size={20} />
                </Button>
              </div>
            </FormGroup>
          </Col>
          <Col md="6" className="mb-0">
            <FormGroup>
              <Label
                style={{
                  fontSize: 12,
                  color: isLightTheme ? "#666" : "#a0a0b2",
                  marginBottom: 4,
                }}
              >
                Use Max Rate
              </Label>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginLeft: "8px",
                }}
              >
                <div className="custom-toggle">
                  <Input
                    id="useMaxRate"
                    name="useMaxRate"
                    type="checkbox"
                    checked={!!settingsData.useMaxRate}
                    onChange={(e) =>
                      handleSettingsChange("useMaxRate", e.target.checked)
                    }
                  />
                  <span className="custom-toggle-slider" />
                </div>
              </div>
            </FormGroup>
          </Col>
        </Row>
      </div>
    </div>
  );

  const renderPrintContent = () => {
    if (printSubView === "viewer" && pdfUrl) {
      return (
        <div className="pt-3 h-100 d-flex flex-column" style={{ height: 'calc(100vh - 100px)' }}>
          <div className="flex-1" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
            <iframe
              key={pdfUrl}
              id="pdf-iframe"
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '600px',
                borderRadius: '4px'
              }}
              title="PDF Viewer"
            />
          </div>
        </div>
      );
    }

    if (printSubView === "documents") {
      const allDocs = filterDocumentsForWorksheetType(
        documentsData?.data || [],
        worksheetData?.type
      );
      const allIds = allDocs.map((d) => d.id);
      const isAllSelected =
        allIds.length > 0 && allIds.every((id) => selectedDocuments.includes(id));

      return (
        <div className="pt-3 h-100 d-flex flex-column">
          <div className="d-flex align-items-center mb-3">
            <h6 className="mb-0 font-weight-bold text-uppercase">
              PRINT DOCUMENTS
            </h6>
          </div>

          <div className="d-flex align-items-center mb-3">
            <div
              className="custom-control custom-checkbox pl-0 cursor-pointer d-flex align-items-center"
              onClick={() => {
                if (isAllSelected) setSelectedDocuments([]);
                else setSelectedDocuments(allIds);
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  border: `2px solid ${isAllSelected
                    ? activeColorStyle.borderColor
                    : isLightTheme
                      ? "#ccc"
                      : "#666"
                    }`,
                  backgroundColor: isAllSelected
                    ? activeColorStyle.borderColor
                    : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 10,
                  transition: "all 0.2s ease",
                }}
              >
                {isAllSelected && <MdCheckCircle size={16} color="#fff" />}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                Select / Deselect All
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-auto" style={{ minHeight: "300px" }}>
            {isDocumentsLoading ? (
              <div className="text-center py-4">Loading documents...</div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                  width: "100%",
                }}
              >
                {allDocs.map((doc) => {
                  const isSelected = selectedDocuments.includes(doc.id);
                  const docName =
                    doc.attributes?.name || doc.name || "Untitled Document";
                  return (
                    <div
                      key={doc.id}
                      className="d-flex align-items-center p-2 rounded cursor-pointer"
                      title={docName} // Tooltip for full name
                      style={{
                        cursor: "pointer",
                        backgroundColor: isLightTheme
                          ? "rgba(0,0,0,0.02)"
                          : "rgba(255,255,255,0.02)",
                        border: `1px solid ${isSelected
                          ? activeColorStyle.borderColor
                          : "transparent"
                          }`,
                        transition: "all 0.2s ease",
                        minWidth: 0, // Critical for grid item truncation
                        width: "100%",
                      }}
                      onClick={() => {
                        setSelectedDocuments((prev) =>
                          prev.includes(doc.id)
                            ? prev.filter((id) => id !== doc.id)
                            : [...prev, doc.id]
                        );
                      }}
                    >
                      <div
                        style={{
                          minWidth: 20,
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          border: `2px solid ${isSelected
                            ? activeColorStyle.borderColor
                            : isLightTheme
                              ? "#ccc"
                              : "#666"
                            }`,
                          backgroundColor: isSelected
                            ? activeColorStyle.borderColor
                            : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 8,
                          transition: "all 0.2s ease",
                        }}
                      >
                        {isSelected && (
                          <MdCheckCircle size={16} color="#fff" />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {docName}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {!isDocumentsLoading && allDocs.length === 0 && (
              <div className="text-center py-4 text-muted">
                No documents found.
              </div>
            )}
          </div>

          <div className="mt-3 d-flex justify-content-end">
            <Button
              style={{ ...activeColorStyle, color: "white" }}
              disabled={
                selectedDocuments.length === 0 ||
                printWorksheetMutation.isPending
              }
              onClick={() => {
                printWorksheetMutation.mutate({
                  id: isWorksheet ? worksheetId : dealId,
                  isWorksheet: !!isWorksheet,
                  documents: selectedDocuments,
                }, {
                  onSuccess: (response) => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);
                    setPdfUrl(blobUrl);
                    setSourceView("documents");
                    setPrintSubView("viewer");
                    setDrawerFullSize(true);
                  }
                });
              }}
            >
              {printWorksheetMutation.isPending
                ? "Printing..."
                : "Print Selected"}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-3 pt-3">
        {[
          "Print Credit Application",
          "Print Test Drive Agreement",
          "Print Trade-in Appraisal",
          "Print Privacy Policy",
          "Print Stmt of Action Taken",
          "Print Notice of Negative Info",
          "Print Worksheet",
          "Print Documents",
          "Print Impact Documents",
        ].filter(item => {
          const shortcuts = [
            "Print Credit Application",
            "Print Test Drive Agreement",
            "Print Trade-in Appraisal",
            "Print Privacy Policy",
            "Print Stmt of Action Taken",
            "Print Notice of Negative Info"
          ];
          if (isWorksheet) {
            return true; // Show everything in the list when inside worksheet
          } else {
            return shortcuts.includes(item); // Show only shortcuts when in deal detail (not worksheet)
          }
        }).map((item, index) => {
          const isDocItem = item === "Print Documents";
          return (
            <div
              key={index}
              className="p-3 mb-2 rounded cursor-pointer d-flex justify-content-between align-items-center"
              onClick={() => {
                if (isDocItem) {
                  setPrintSubView("documents");
                } else if (item === "Print Worksheet") {
                  // Client-side PDF generation for worksheet
                  if (isWorksheet && worksheetId) {
                    // Get worksheet data from worksheetData
                    if (worksheetData) {
                      import('views/components/Deals/WorksheetPrintView').then(({ generateWorksheetPDF }) => {
                        const { blobUrl } = generateWorksheetPDF(worksheetData);
                        setPdfUrl(blobUrl);
                        setSourceView("menu");
                        setPrintSubView("viewer");
                        setDrawerFullSize(true);
                      }).catch((err) => {
                        console.error('Error generating worksheet PDF:', err);
                        showNotification('error', 'Failed to generate worksheet PDF');
                      });
                    }
                  } else {
                    showNotification('warning', 'Please select a worksheet to print');
                  }
                } else if (item.startsWith("Print ")) {
                  if (isWorksheet && !worksheetId) return;
                  if (!isWorksheet && !dealId) return;

                  // Convert e.g. "Print Test Drive Agreement" -> "test_drive_agreement"
                  let documentName = item
                    .replace("Print ", "")
                    .trim()
                    .toLowerCase()
                    .replace(/ /g, '_')
                    .replace(/-/g, '_');

                  // Custom mapping for specific requirements
                  if (item === "Print Privacy Policy") {
                    documentName = "privacy_notice";
                  } else if (item === "Print Notice of Negative Info") {
                    documentName = "notice_of_negative_info";
                  }

                  printWorksheetMutation.mutate({
                    id: isWorksheet ? worksheetId : dealId,
                    isWorksheet: !!isWorksheet,
                    documents: null,
                    documentName: documentName
                  }, {
                    onSuccess: (response) => {
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const blobUrl = URL.createObjectURL(blob);
                      setPdfUrl(blobUrl);
                      setSourceView("menu");
                      setPrintSubView("viewer");
                      setDrawerFullSize(true);
                    }
                  });
                }
              }}
              style={{
                backgroundColor: isLightTheme
                  ? "rgba(0,0,0,0.05)"
                  : "rgba(255,255,255,0.05)",
                cursor: "pointer",
                transition: "all 0.2s ease",
                border: "1px solid transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isLightTheme
                  ? "rgba(0,0,0,0.08)"
                  : "rgba(255,255,255,0.08)";
                e.currentTarget.style.borderColor =
                  activeColorStyle.borderColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isLightTheme
                  ? "rgba(0,0,0,0.05)"
                  : "rgba(255,255,255,0.05)";
                e.currentTarget.style.borderColor = "transparent";
              }}
            >
              <h6
                className="mb-0"
                style={{
                  fontWeight: 400,
                  color: isLightTheme ? "#1e1e2f" : "#fff",
                }}
              >
                {item}
              </h6>
              {(isDocItem || item.startsWith("Print ")) && <MdChevronRight size={20} />}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSendContent = () => {
    if (sendView === "text_auth") {
      const applicantName = `${applicantData?.full_name || ""}`.trim().toUpperCase() + " [PRIMARY]";
      const rawCellPhone = applicantData?.cell_phone || applicantData?.phone;
      const hasPhone = rawCellPhone && rawCellPhone !== "-" && rawCellPhone.trim() !== "";
      const cellPhone = hasPhone ? rawCellPhone : "Phone not available";
      const allowAccounting = applicantData?.allow_accounting;
      const allowMarketing = applicantData?.allow_marketing;
      const smsStatus = applicantData?.sms_status || "not_send";

      // Helper function to get status badge
      const getStatusBadge = (status) => {
        const statusConfig = {
          not_send: { color: "#6c757d", text: "Not Sent", bgColor: "rgba(108, 117, 125, 0.15)" },
          waiting: { color: "#ffc107", text: "Waiting", bgColor: "rgba(255, 193, 7, 0.15)" },
          approved: { color: "#00f2c3", text: "Approved", bgColor: "rgba(0, 242, 195, 0.15)" },
          rejected: { color: "#fd5d93", text: "Rejected", bgColor: "rgba(253, 93, 147, 0.15)" }
        };
        const config = statusConfig[status] || statusConfig.not_send;
        return (
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "6px 16px",
            backgroundColor: config.bgColor,
            fontSize: "11px",
            fontWeight: "700",
            color: config.color,
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            borderLeft: `3px solid ${config.color}`
          }}>
            {config.text}
          </div>
        );
      };

      return (
        <div className="pt-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <h5 className="mb-0 font-weight-bold text-uppercase mr-2">SEND TEXT</h5>
              <MdInfo size={18} color="#1f8ef1" style={{ cursor: "pointer" }} />
            </div>
            {getStatusBadge(smsStatus)}
          </div>

          <div
            className="p-3 mb-4"
            style={{
              backgroundColor: isLightTheme ? "#f8f9fa" : "#2b2b3d",
              borderLeft: `4px solid ${activeColorStyle.borderColor}`
            }}
          >
            <div className="d-flex align-items-center">
              <div className="flex-grow-1">
                <span className="d-block text-uppercase mb-1" style={{ fontSize: "10px", fontWeight: 700, color: isLightTheme ? "#888" : "#ccc", letterSpacing: "0.5px" }}>Applicant</span>
                <h6 className="mb-1 font-weight-bold">{applicantName}</h6>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px", fontSize: "12px" }}>
                  {hasPhone && <span>Cell Phone</span>}
                  <span className={`font-weight-bold ${!hasPhone ? "text-danger" : ""}`}>{cellPhone}</span>

                  {hasPhone && (
                    <>
                      <span className={`d-flex align-items-center ${allowAccounting ? "text-success" : "text-danger"}`}>
                        {allowAccounting ? <MdCheckCircle className="mr-1" /> : <IoMdCloseCircle className="mr-1" />} Account
                      </span>

                      <span className={`d-flex align-items-center ${allowMarketing ? "text-success" : "text-danger"}`}>
                        {allowMarketing ? <MdCheckCircle className="mr-1" /> : <IoMdCloseCircle className="mr-1" />} Marketing
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {
            hasPhone && (
              <div
                className="p-0 overflow-hidden mb-3"
                style={{
                  backgroundColor: isLightTheme ? "#f8f9fa" : "#2b2b3d",
                  borderLeft: `4px solid #1f8ef1`
                }}
              >
                <div
                  className="p-2 d-flex justify-content-between align-items-center"
                  style={{ backgroundColor: "#1f8ef1", color: "white" }}
                >
                  <h6 className="mb-0 font-weight-bold text-uppercase" style={{ color: "white" }}>TEXTING AUTHORIZATION</h6>
                </div>

                <div className="p-3">
                  {!allowAccounting && !allowMarketing && (
                    <>
                      <p className="small mb-3">
                        In order to receive texts, you must first perform the required authorization steps below.
                      </p>
                      <ul className="small mb-3 pl-3">
                        <li>Click the Authorize button.</li>
                        <li>Once clicked, the customer will receive a text with an approval #.</li>
                        <li>Ask for and enter this approval #, then click the Verify button.</li>
                      </ul>
                      <p className="small font-italic mb-4">
                        Note: The customer can also reply "Agree" to the authorization text.
                      </p>
                    </>
                  )}

                  {/* Authorization Status */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      {allowAccounting ? (
                        <MdCheckCircle className="text-success mr-2" size={16} />
                      ) : (
                        <IoMdCloseCircle className="text-danger mr-2" size={16} />
                      )}
                      <span className="small font-weight-bold">Allow Accounting Texting</span>
                    </div>
                    <div className="d-flex align-items-center">
                      {allowMarketing ? (
                        <MdCheckCircle className="text-success mr-2" size={16} />
                      ) : (
                        <IoMdCloseCircle className="text-danger mr-2" size={16} />
                      )}
                      <span className="small font-weight-bold">Allow Marketing Texting</span>
                    </div>
                  </div>

                  {/* Single Authorization Section */}
                  {(!allowAccounting || !allowMarketing) && (
                    <div className="border-top pt-3 mt-3">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <span className="font-weight-bold">Authorization Required</span>
                        <Button size="sm" color="info" className="mb-0" onClick={handleSendAuthorization} disabled={sendDealSMSMutation.isPending}>
                          {sendDealSMSMutation.isPending ? "SENDING..." : "AUTHORIZE"}
                        </Button>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="small mr-2">Approval #</span>
                        <Input
                          type="text"
                          bsSize="sm"
                          style={{ width: "100px", marginRight: "10px" }}
                          value={approvalCode}
                          maxLength={5}
                          onChange={(e) => setApprovalCode(e.target.value)}
                          placeholder="Enter code"
                        />
                        <Button
                          size="sm"
                          color="primary"
                          className="mb-0"
                          onClick={handleVerifyApprovalCode}
                          disabled={updateDealMutation.isPending || !approvalCode.trim()}
                        >
                          {updateDealMutation.isPending ? "VERIFYING..." : "VERIFY"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Both Approved */}
                  {allowAccounting && allowMarketing && (
                    <div className="d-flex align-items-center p-3 mt-3" style={{
                      backgroundColor: isLightTheme ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.05)",
                      borderLeft: "4px solid #00f2c3"
                    }}>
                      <MdCheckCircle className="mr-2" size={20} style={{ color: "#00f2c3" }} />
                      <span className="font-weight-bold" style={{ color: isLightTheme ? "#1e1e2f" : "#fff", fontSize: "13px", letterSpacing: "0.3px" }}>Texting Fully Authorized</span>
                    </div>
                  )}

                </div>
              </div>
            )
          }
        </div >
      );
    }

    if (sendView === "email") {
      return (
        <SendEmail
          key={sendEmailData?.transaction_id || "email-send"}
          isLightTheme={isLightTheme}
          activeColor={activeColor}
          isLoading={sendMailMutation.isPending}
          customerEmail={applicantData?.email}
          initialSubject={sendEmailData?.subject}
          initialMessage={sendEmailData?.message}
          librarySource="deals"
          recordId={dealId}
          onCancel={() => {
            setSendView("options");
            setSendEmailData(null);
          }}
          onSend={(data) => {
            sendMailMutation.mutate({
              type: "deals",
              id: dealId,
              subject: data?.subject,
              message: data?.message,
              template: data?.template?.value,
              attachments: data?.attachments,
            }, {
              onSuccess: () => {
                setSendView("options");
                setSendEmailData(null);
                closeDrawer();
              }
            });
          }}
        />
      );
    }

    return (
      <div className="d-flex flex-column gap-3 pt-3">
        {[
          {
            id: "email",
            label: "Send an Email",
            icon: <MdEmail size={20} />,
            disabled: !applicantData?.email,
            disabledText: t('transactions.missingEmail')
          },
          { id: "text", label: "Send a Text", icon: <MdMessage size={20} /> },
        ].map((item, index) => (
          <div
            key={index}
            className={`p-3 mb-2 rounded d-flex align-items-center gap-3 ${item.disabled ? "opacity-5" : "cursor-pointer"}`}
            style={{
              backgroundColor: isLightTheme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)",
              border: "1px solid transparent",
              transition: "all 0.2s ease",
              cursor: item.disabled ? "not-allowed" : "pointer",
              opacity: item.disabled ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (item.disabled) return;
              e.currentTarget.style.borderColor = activeColorStyle.borderColor;
              e.currentTarget.style.backgroundColor = isLightTheme ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)";
            }}
            onMouseLeave={(e) => {
              if (item.disabled) return;
              e.currentTarget.style.borderColor = "transparent";
              e.currentTarget.style.backgroundColor = isLightTheme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.05)";
            }}
            onClick={() => {
              if (item.disabled) return;
              if (item.id === "text") {
                setSendView("text_auth");
              } else if (item.id === "email") {
                setSendView("email");
              }
            }}
          >
            <div style={{ color: item.disabled ? "#ccc" : activeColorStyle.borderColor, marginRight: "12px" }}>
              {item.icon}
            </div>
            <div className="d-flex flex-column">
              <h6 className="mb-0" style={{
                fontWeight: 600,
                color: item.disabled ? "#999" : (isLightTheme ? "#1e1e2f" : "#fff")
              }}>
                {item.label}
              </h6>
              {item.disabled && (
                <span style={{ fontSize: '11px', color: '#ff3d3d', marginTop: '2px' }}>
                  {item.disabledText}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAddDesiredVehicle = () => (
    <div
      className="deals-drawer-add-vehicle"
      style={{ height: "calc(100vh - 120px)", overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <AddVehicle
        activeColor={activeColor}
        showEmptyOption={true}
        isLightTheme={isLightTheme}
        isTheme2={isTheme2}
        onClose={() => setDrawerSubView(null)}
        onVehicleAdded={(vehicles) => {
          if (vehicles && vehicles.length > 0) {
            const selectedVehicle = vehicles[0];
            updateDealMutation.mutate({
              id: dealId,
              data: {
                desired_vehicle: selectedVehicle.is_empty
                  ? { is_empty: true }
                  : { documentId: selectedVehicle.documentId }
              }
            });
          }
          if (onAddDesiredVehicle) onAddDesiredVehicle(vehicles);
          setDrawerSubView(null);
        }}
      />
    </div>
  );

  const renderMoreContent = () => (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
        {[
          {
            label: "Add Trade in 1",
            action: () => {
              if (onAddTradeIn) onAddTradeIn();
              closeDrawer();
            },
            icon: <MdAdd size={20} />,
            visible: !hasTradeIn1
          },
          {
            label: "Remove Trade in 1",
            action: () => {
              if (onRemoveTradeIn1) onRemoveTradeIn1();
              closeDrawer();
            },
            icon: <MdRemove size={20} />,
            visible: hasTradeIn1
          },
          {
            label: "Add Trade in 2",
            action: () => {
              if (onAddTradeIn2) onAddTradeIn2();
              closeDrawer();
            },
            icon: <MdAdd size={20} />,
            visible: hasTradeIn1 && !hasTradeIn2
          },
          {
            label: "Remove Trade in 2",
            action: () => {
              if (onRemoveTradeIn2) onRemoveTradeIn2();
              closeDrawer();
            },
            icon: <MdRemove size={20} />,
            visible: hasTradeIn2
          },
          {
            label: "Add Desired Vehicle",
            action: () => {
              setDrawerSubView("add_desired_vehicle");
              setDrawerFullSize(true);
            },
            icon: <MdAdd size={20} />,
            visible: !hasDesiredVehicle
          },
          {
            label: "Remove Desired Vehicle",
            action: () => {
              if (onRemoveDesiredVehicle) onRemoveDesiredVehicle();
              closeDrawer();
            },
            icon: <MdRemove size={20} />,
            visible: hasDesiredVehicle
          }
        ].filter(item => item.visible).map((item, index) => (
          <div
            key={index}
            onClick={item.action}
            className="d-flex align-items-center justify-content-between"
            style={{
              padding: "16px 20px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "8px",
              border: isLightTheme
                ? "1px solid #e0e0e0"
                : "1px solid rgba(255, 255, 255, 0.1)",
              backgroundColor: isLightTheme
                ? "#fff"
                : "rgba(255, 255, 255, 0.05)",
              color: isLightTheme ? "#333" : "#fff",
              transition: "all 0.2s ease",
              boxShadow: isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isLightTheme
                ? "#f8f9fa"
                : "rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = isLightTheme ? "0 4px 8px rgba(0,0,0,0.05)" : "0 4px 8px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isLightTheme
                ? "#fff"
                : "rgba(255, 255, 255, 0.05)";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = isLightTheme ? "0 2px 4px rgba(0,0,0,0.02)" : "none";
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                color: activeColorStyle.borderColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {item.icon}
              </div>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    if (drawerType === "deals") {
      return renderDealsContent();
    }

    if (drawerType === "settings") {
      return renderSettingsContent();
    }

    if (drawerType === "print") {
      return renderPrintContent();
    }

    if (drawerType === "send") {
      return renderSendContent();
    }

    if (drawerType === "more") {
      if (drawerSubView === "add_desired_vehicle") {
        return renderAddDesiredVehicle();
      }
      return renderMoreContent();
    }

    return null;
  };

  if (!isDrawerVisible) {
    return null;
  }

  return (
    <>
      <div
        style={{
          ...drawerStyles.overlay,
          opacity: drawerOpen ? 1 : 0,
          pointerEvents: drawerOpen ? "auto" : "none",
        }}
        onClick={closeDrawer}
      />
      <div
        className={
          isTheme2
            ? "deals-right-drawer deals-right-drawer--theme2"
            : "deals-right-drawer"
        }
        style={{
          ...drawerStyles.panel,
          transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {drawerFormFieldsCss ? <style>{drawerFormFieldsCss}</style> : null}
        {!isMobile && (
          <div
            style={drawerStyles.drawerResizeHandle}
            onPointerDown={onDrawerWidthHandlePointerDown}
            title={t("accountsDrawer.drawerResizeTitle")}
            role="separator"
            aria-orientation="vertical"
            aria-label={t("accountsDrawer.drawerResizeAria")}
          >
            <div
              style={{
                width: 3,
                height: 40,
                backgroundColor: activeColorStyle.borderColor,
                opacity: 0.55,
              }}
            />
          </div>
        )}
        <div style={drawerStyles.mainColumn}>
        <div style={drawerStyles.header}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              minWidth: 0,
            }}
          >
            {(
              drawerSubView !== null ||
              (drawerType === "print" && printSubView !== "menu") ||
              (drawerType === "send" && sendView !== "options")
            ) && (
                <button
                  className="btn btn-link p-0 mr-3"
                  style={{ color: activeColorStyle.borderColor, fontWeight: 600, fontSize: 13 }}
                  onClick={() => {
                    if (drawerSubView) {
                      setDrawerSubView(null);
                      setDrawerFullSize(false);
                      return;
                    }
                    if (drawerType === "print") {
                      if (printSubView === "viewer") {
                        setPrintSubView(sourceView);
                        setDrawerFullSize(false);
                        if (pdfUrl) {
                          URL.revokeObjectURL(pdfUrl);
                          setPdfUrl(null);
                        }
                      } else {
                        setPrintSubView("menu");
                      }
                    } else if (drawerType === "send") {
                      setSendView("options");
                    }
                  }}
                >
                  &lt; {t('common.back')}
                </button>
              )}
            {drawerType === "deals" ? (
              <h5 style={{ margin: 0, flex: 1, minWidth: 0 }}>
                <Nav tabs className="border-0" style={{ marginTop: "4px" }}>
                  {[
                    { id: "contract", label: t("dealsDrawer.tabs.contract") },
                    ...(![
                      "contracted",
                      "contracted_booked",
                      "loan_booked",
                    ].includes(worksheetData?.worksheet_status)
                      ? [
                        {
                          id: "decision",
                          label: t("dealsDrawer.tabs.decision"),
                        },
                      ]
                      : []),
                  ].map((tab) => (
                    <NavItem key={tab.id}>
                      <NavLink
                        href="#"
                        className={
                          drawerActiveTab === tab.id
                            ? "active font-weight-bold"
                            : ""
                        }
                        style={{
                          background: "none",
                          border: "none",
                          fontSize: 12,
                          cursor: "pointer",
                          color:
                            drawerActiveTab === tab.id
                              ? activeColorStyle.borderColor
                              : isLightTheme
                                ? "#1e1e2f"
                                : "#fff",
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          setDrawerActiveTab(tab.id);
                        }}
                      >
                        {tab.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </h5>
            ) : (
              <h5 style={{ margin: 0, flex: 1, minWidth: 0 }}>
                <Nav tabs className="border-0" style={{ marginTop: "4px" }}>
                  <NavItem>
                    <span
                      className="nav-link active font-weight-bold"
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 12,
                        cursor: "default",
                        color: activeColorStyle.borderColor,
                        userSelect: "none",
                      }}
                    >
                      {dealsDrawerStaticHeaderTitle}
                    </span>
                  </NavItem>
                </Nav>
              </h5>
            )}
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            style={drawerStyles.closeBtn}
          >
            {t("dealsDrawer.close")}
          </button>
        </div>
        <div
          style={drawerStyles.body}
          className={isTheme2 ? "deals-right-drawer-form-fields" : undefined}
        >
          {renderContent()}
        </div>
        </div>
      </div>
    </>
  );
});

export default DealsRightDrawer;
